import type { MenuItem } from './types';

export const findMenuItemByLink = (items: MenuItem[], link: string): MenuItem | null => {
  // Normalize links by removing trailing slashes for comparison
  const normalizePath = (path: string | null) => {
    if (!path) return null;
    return path.endsWith('/') ? path.slice(0, -1) : path;
  };

  const normalizedLink = normalizePath(link);

  // First, try exact match (with normalization)
  for (const item of items) {
    const normalizedItemLink = normalizePath(item.link);
    if (normalizedItemLink === normalizedLink) {
      return item;
    }
    if (item.childs.length > 0) {
      const found = findMenuItemByLink(item.childs, link);
      if (found) return found;
    }
  }

  return null;
};

/**
 * Finds the parent menu item of a given menu item
 * Used for getting sibling navigation (GetMenuItemStructure expects parent ID)
 */
export const findParentMenuItem = (
  items: MenuItem[],
  targetItemId: number,
  parent: MenuItem | null = null
): MenuItem | null => {
  for (const item of items) {
    // Check if this item's children contain the target
    if (item.childs.some((child) => child.id === targetItemId)) {
      return item;
    }

    // Recursively search in children
    if (item.childs.length > 0) {
      const found = findParentMenuItem(item.childs, targetItemId, item);
      if (found) return found;
    }
  }

  return parent;
};

/**
 * Finds a menu item whose link is a direct ancestor (prefix) of the target path
 * Used to match nested child routes that live under a menu item's path
 *
 * Example:
 *   Menu has: /reports/work-log/
 *   Target: /reports/work-log/details/
 *   Returns: the "Work log" menu item (most specific ancestor wins)
 */
export const findAncestorMenuItem = (items: MenuItem[], targetPath: string): MenuItem | null => {
  const normalizePath = (path: string | null) => {
    if (!path) return null;
    return path.endsWith('/') ? path.slice(0, -1) : path;
  };

  const normalizedTarget = normalizePath(targetPath);
  if (!normalizedTarget) return null;

  let bestMatch: MenuItem | null = null;
  let bestMatchLength = 0;

  const searchItems = (items: MenuItem[]) => {
    for (const item of items) {
      const itemLink = normalizePath(item.link);
      if (
        itemLink &&
        normalizedTarget.startsWith(itemLink + '/') &&
        itemLink.length > bestMatchLength
      ) {
        bestMatch = item;
        bestMatchLength = itemLink.length;
      }
      if (item.childs.length > 0) {
        searchItems(item.childs);
      }
    }
  };

  searchItems(items);
  return bestMatch;
};

/**
 * Finds a menu item that shares the same parent directory path with the target
 * Used to match child grid routes (siblings) that aren't directly in menu
 *
 * Example:
 *   Menu has: /management/projects/projects/
 *   Target: /management/projects/phase/
 *   Both have parent: /management/projects/
 *   Returns: menu item for "Projects"
 */
export const findParentPathMenuItem = (items: MenuItem[], targetPath: string): MenuItem | null => {
  const normalizePath = (path: string | null) => {
    if (!path) return null;
    return path.endsWith('/') ? path.slice(0, -1) : path;
  };

  const getParentPath = (path: string) => {
    const segments = path.split('/').filter(Boolean);
    if (segments.length <= 1) return null;
    return '/' + segments.slice(0, -1).join('/');
  };

  const normalizedTarget = normalizePath(targetPath);
  if (!normalizedTarget) return null;

  const targetParentPath = getParentPath(normalizedTarget);
  if (!targetParentPath) return null;

  // Helper to search recursively through menu items
  const searchItems = (items: MenuItem[]): MenuItem | null => {
    for (const item of items) {
      const itemLink = normalizePath(item.link);
      if (itemLink) {
        const itemParentPath = getParentPath(itemLink);
        // Check if this menu item shares the same parent directory
        if (itemParentPath === targetParentPath) {
          return item;
        }
      }

      // Recursively search children
      if (item.childs.length > 0) {
        const found = searchItems(item.childs);
        if (found) return found;
      }
    }
    return null;
  };

  return searchItems(items);
};

/**
 * Finds a menu item whose linkId matches the given value.
 * Used to resolve visible menu items that are shortcuts pointing to a content node.
 */
export const findMenuItemByLinkId = (items: MenuItem[], linkId: number): MenuItem | null => {
  for (const item of items) {
    if (item.linkId === linkId) return item;
    if (item.childs.length > 0) {
      const found = findMenuItemByLinkId(item.childs, linkId);
      if (found) return found;
    }
  }
  return null;
};
